import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Grid,
  Column,
  TextInput,
  Tile,
  Stack,
  ToastNotification,
  Button,
} from "@carbon/react";
import {
  GoogleMap,
  Marker,
  Autocomplete,
  LoadScript,
} from "@react-google-maps/api";
import { callApi } from "../../../services/apiService";
import CommonFields from "../../property/common/CommonField";

// GMAP Configuration
const mapContainerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "8px",
  border: "1px solid #e0e0e0",
};

const defaultCenter = {
  lat: 8.0883,
  lng: 77.5385,
};

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const LocationDetails = ({
  propertyUuid = "",
  onSaveSuccess,
  CommonButtons,
  onNavigateNext,
  onBack,
}) => {
  // Form State
  const [form, setForm] = useState({
    city: "Kanyakumari",
    taluk: "",
    village: "",
    area: "",
    userVillageName: "",
    areaName: "",
    landmark: "",
    addressLine1: "",
    addressLine2: "",
    pincode: "",
    locationId: "",
    latitude: 0,
    longitude: 0,
  });

  const [taluks, setTaluks] = useState([]);
  const [villages, setVillages] = useState([]);
  const [areas, setAreas] = useState([]);
  const [filteredVillages, setFilteredVillages] = useState([]);
  const [filteredAreas, setFilteredAreas] = useState([]);

  const [isCustomAreaName, setIsCustomAreaName] = useState(false);

  // GMAP States
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [searchAddress, setSearchAddress] = useState("");
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [gmapError, setGmapError] = useState("");
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const geocoderRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [touched, setTouched] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [googlePlaceResponse, setGooglePlaceResponse] = useState(null);

  // Initialize Geocoder when script loads
  useEffect(() => {
    if (isScriptLoaded && window.google) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [isScriptLoaded]);

  const validationRules = useMemo(
    () => ({
      taluk: { required: true, message: "Taluk is required" },
      village: {
        required: true,
        message: "Please select the correct village from Search Village",
      },
      userVillageName: { required: true, message: "Village name is required" },

      area: { required: false, message: "Area is required" },
      landmark: { required: false, message: "Landmark is required" },
      addressLine1: { required: false, message: "Address Line 1 is required" },
      pincode: {
        required: false,
        message: "Pincode is required",
        custom: (v) => {
          if (!v) return null;
          if (!/^\d+$/.test(v)) return "Pincode must contain only numbers";
          if (v.length !== 6) return "Pincode must be exactly 6 digits";
          return null;
        },
      },
    }),
    []
  );

  const requiredLabel = (text) => (
    <>
      {text} <span style={{ color: "#da1e28" }}>*</span>
    </>
  );

  const getError = useCallback(
    (field) => {
      const rule = validationRules[field];
      const value = form[field];

      if (rule?.required && !value) return rule.message;
      if (rule?.custom) return rule.custom(value);
      return null;
    },
    [form, validationRules]
  );

  const hasErrors = useCallback(
    () => Object.keys(validationRules).some((f) => getError(f)),
    [getError, validationRules]
  );

  const handleBlur = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  // Load Master Data
  useEffect(() => {
    const loadMasters = async () => {
      try {
        const t = await callApi("GET", "/taluk-masters/");
        setTaluks(t?.data ?? []);

        const v = await callApi("GET", "/villages/");
        setVillages(v?.data ?? []);

        const a = await callApi("GET", "/areas/");
        setAreas(a?.data ?? []);
      } catch (err) {
        setError("Failed to load master data");
      }
    };
    loadMasters();
  }, []);

  // ✅ FIX 1: getAddressFromCoords — updates BOTH search box and areaName
  const getAddressFromCoords = useCallback((coords) => {
    if (!geocoderRef.current) return;

    geocoderRef.current.geocode({ location: coords }, (results, status) => {
      if (status === "OK" && results[0]) {
        const formatted = results[0].formatted_address || "";
        setSearchAddress(formatted); // updates "Search Location" box
        setForm((prev) => ({ ...prev, areaName: formatted })); // saves to MAP_LOCATION_NAME
      }
    });
  }, []);

  // Fetch Existing Data
  const fetchExisting = useCallback(async () => {
    if (!propertyUuid) {
      setIsLoaded(true);
      return;
    }

    try {
      const resp = await callApi(
        "GET",
        `/property-locations/?property_uuid=${propertyUuid}&request_type=form_attributes`
      );

      const raw = Array.isArray(resp?.data) ? resp.data[0] : resp?.data || null;

      if (!raw) {
        setIsLoaded(true);
        return;
      }

      // ✅ FIX 4: read MAP_LOCATION_NAME and populate search box
      const mapLocationFromApi = raw.MAP_LOCATION_NAME || "";
      const areaNameFromApi = raw.AREA_NAME || mapLocationFromApi;
      const areaIdFromApi = raw.AREA_ID ? String(raw.AREA_ID) : "";

      if (mapLocationFromApi) {
        setSearchAddress(mapLocationFromApi);
      }

      const newForm = {
        city: "Kanyakumari",
        taluk: raw.TALUK_ID ? String(raw.TALUK_ID) : "",
        village: raw.VILLAGE_ID ? String(raw.VILLAGE_ID) : "",
        userVillageName: raw.village_name || "",
        area: areaIdFromApi,
        areaName: areaNameFromApi,
        landmark: raw.LANDMARK || "",
        addressLine1: raw.ADDRESS_LINE_1 || "",
        addressLine2: raw.ADDRESS_LINE_2 || "",
        pincode: raw.PIN_CODE || "",
        locationId:
          raw.PROPERTY_LOCATION_ID || raw.LOCATION_ID || raw.id || "",
        latitude: raw.LATITUDE || 0,
        longitude: raw.LONGITUDE || 0,
      };

      setForm(newForm);

      if (
        raw.LATITUDE &&
        raw.LONGITUDE &&
        raw.LATITUDE !== 0 &&
        raw.LONGITUDE !== 0
      ) {
        const coords = {
          lat: parseFloat(raw.LATITUDE),
          lng: parseFloat(raw.LONGITUDE),
        };
        setMapCenter(coords);
        setMarkerPosition(coords);

        if (isScriptLoaded && geocoderRef.current) {
          getAddressFromCoords(coords);
        }
      }

      setIsLoaded(true);
    } catch (err) {
      setError("Failed to load location details");
      setIsLoaded(true);
    }
  }, [propertyUuid, isScriptLoaded, getAddressFromCoords]);

  // Save Handler
  const handleSave = useCallback(
    async (navigateNext = false) => {
      setTouched({
        taluk: true,
        village: true,
        userVillageName: true,
        area: true,
        landmark: true,
        addressLine1: true,
        pincode: true,
      });

      if (hasErrors()) {
        setError("Please fill required fields");
        return;
      }

      setSaving(true);
      setError("");
      setSuccess("");

      try {
        if (googlePlaceResponse) {
          try {
            await callApi(
              "POST",
              "/master-extraction/extract-masters",
              googlePlaceResponse
            );
          } catch (err) {
            console.error("Extract API failed", err);
          }
        }

        const body = {
          property_uuid: propertyUuid,
          location: {
            landmark: form.landmark,
            address_line_1: form.addressLine1,
            address_line_2: form.addressLine2,
            country_id: 1,
            state_id: 1,
            district_id: 1,
            taluk_id: Number(form.taluk) || null,
            village_id: Number(form.village) || null,
            village_name: form.userVillageName || "",
            area_id: Number(form.area) || 0,
            map_location_name: form.areaName, // ← now populated
            latitude: form.latitude || 0,
            longitude: form.longitude || 0,
            pin_code: form.pincode,
          },
        };

        const isUpdate = !!form.locationId;
        if (isUpdate) body.location.location_id = form.locationId;

        const method = isUpdate ? "PUT" : "POST";

        const locationResponse = await callApi(
          method,
          "/property-locations/",
          body
        );

        await fetchExisting();

        const savedLocation = locationResponse?.data || {};

        const propertyId =
          savedLocation.PROPERTY_ID || savedLocation.property_id || null;

        const villageName =
          savedLocation.VILLAGE_NAME ||
          savedLocation.village_name ||
          form.villageName ||
          "";

        const updatedLocationData = {
          ...form,
          villageName,
          location: {
            village_name: villageName,
            villageName,
          },
        };

        console.log("📍 LOCATION SAVED:", {
          propertyId,
          villageName,
          locationResponse,
        });

        setSuccess(
          isUpdate
            ? "Location details saved successfully"
            : "Location details saved successfully"
        );

        onSaveSuccess?.(propertyUuid, updatedLocationData, 2, propertyId);

        if (navigateNext) {
          onNavigateNext?.(form);
        }
      } catch (err) {
        setError(err.message || "Failed to save location details");
      } finally {
        setSaving(false);
      }
    },
    [
      form,
      propertyUuid,
      fetchExisting,
      onSaveSuccess,
      onNavigateNext,
      hasErrors,
      googlePlaceResponse,
    ]
  );

  useEffect(() => {
    if (propertyUuid && !isLoaded) {
      fetchExisting();
    } else if (!propertyUuid) {
      setIsLoaded(true);
    }
  }, [propertyUuid, fetchExisting, isLoaded]);

  // Filter Villages & Areas
  useEffect(() => {
    if (!form.taluk) {
      setFilteredVillages([]);
      return;
    }

    const filteredVills = villages.filter((v) => {
      const talukId = v.TALUK_ID || v.taluk_id || v.talukId;
      return String(talukId) === String(form.taluk);
    });

    setFilteredVillages(filteredVills);

    if (form.village) {
      const selectedVillage = villages.find((v) => {
        const villageId = v.VILLAGE_ID || v.village_id || v.id;
        return String(villageId) === String(form.village);
      });

      const villageTalukId =
        selectedVillage?.TALUK_ID ||
        selectedVillage?.taluk_id ||
        selectedVillage?.talukId;

      if (selectedVillage && String(villageTalukId) !== String(form.taluk)) {
        setForm((prev) => ({ ...prev, village: "", area: "", areaName: "" }));
        setIsCustomAreaName(false);
      }
    }
  }, [form.taluk, form.village, villages]);

  useEffect(() => {
    if (!form.village) {
      setFilteredAreas([]);
      return;
    }

    const filteredArs = areas.filter((a) => {
      const villageId = a.VILLAGE_ID || a.village_id || a.villageId;
      return String(villageId) === String(form.village);
    });

    setFilteredAreas(filteredArs);

    if (form.areaName && isCustomAreaName) {
      // Preserve custom area name
    } else if (form.area && filteredArs.length > 0) {
      const areaExists = filteredArs.some((a) => {
        const areaId = a.AREA_ID || a.id || a.area_id;
        return String(areaId) === String(form.area);
      });

      if (!areaExists) {
        setForm((prev) => ({ ...prev, area: "", areaName: "" }));
        setIsCustomAreaName(false);
      }
    }
  }, [form.village, form.area, form.areaName, areas, isCustomAreaName]);

  // GMAP Functions
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onMapClick = useCallback(
    (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      const newPosition = { lat, lng };

      setMarkerPosition(newPosition);
      setMapCenter(newPosition);

      setForm((prev) => ({
        ...prev,
        latitude: lat,
        longitude: lng,
      }));

      if (geocoderRef.current) {
        getAddressFromCoords(newPosition);
      }
    },
    [getAddressFromCoords]
  );

  const onLoadError = useCallback(() => {
    setGmapError("Failed to load Google Maps");
  }, []);

  const onScriptLoad = useCallback(() => {
    setIsScriptLoaded(true);
    setGmapError("");
  }, []);

  const serializeBounds = (bounds) => {
    if (!bounds) return undefined;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    return {
      northeast: { lat: ne.lat(), lng: ne.lng() },
      southwest: { lat: sw.lat(), lng: sw.lng() },
    };
  };

  // ✅ FIX 2 + FIX 3: handlePlaceSelect now sets search box + areaName, and doesn't wipe values
  const handlePlaceSelect = useCallback(async () => {
    if (!autocompleteRef.current || !geocoderRef.current) return;

    const place = autocompleteRef.current.getPlace();

    if (!place.geometry || !place.geometry.location) {
      setGmapError("No location found");
      return;
    }

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    const newPosition = { lat, lng };

    setMarkerPosition(newPosition);
    setMapCenter(newPosition);

    const formattedAddress = place.formatted_address || place.name || "";
    setSearchAddress(formattedAddress); // updates Search Location box

    setForm((prev) => ({
      ...prev,
      latitude: lat,
      longitude: lng,
      areaName: formattedAddress, // saves to MAP_LOCATION_NAME
    }));

    geocoderRef.current.geocode({ location: newPosition }, async (results, status) => {
      if (status !== "OK" || !results?.length) {
        setGmapError("Failed to fetch geocoder details");
        return;
      }

      const extractedPlusCode =
        results.find((r) => r.plus_code)?.plus_code || {};

      const geocoderResponse = {
        plus_code: extractedPlusCode,
        results: results.map((r) => ({
          ...r,
          geometry: {
            ...r.geometry,
            viewport: serializeBounds(r.geometry?.viewport),
            bounds: r.geometry?.bounds
              ? serializeBounds(r.geometry.bounds)
              : undefined,
          },
        })),
        status: status || "OK",
      };

      setGooglePlaceResponse(geocoderResponse);

      try {
        const response = await callApi(
          "POST",
          "/master-extraction/extract-masters",
          geocoderResponse
        );

        const extracted = response?.data?.data || response?.data || {};

        setForm((prev) => ({
          ...prev,
          taluk: extracted?.taluk_id
            ? String(extracted.taluk_id)
            : prev.taluk,
          village: extracted?.village_id
            ? String(extracted.village_id)
            : prev.village,
          area: extracted?.area_id
            ? String(extracted.area_id)
            : prev.area,
          areaName: prev.areaName, // never blank out the search address
        }));
      } catch (err) {
        console.error("Master extraction failed", err);
      }
    });
  }, []);

  const onAutocompleteLoad = useCallback((autocomplete) => {
    autocompleteRef.current = autocomplete;
    autocomplete.setFields([
      "address_components",
      "geometry",
      "formatted_address",
    ]);
  }, []);

  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGmapError("Geolocation not supported");
      return;
    }

    setIsSearchingLocation(true);
    setGmapError("");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        setMarkerPosition(coords);
        setMapCenter(coords);
        setForm((prev) => ({
          ...prev,
          latitude: coords.lat,
          longitude: coords.lng,
        }));

        if (geocoderRef.current) {
          getAddressFromCoords(coords); // updates search box + areaName
        }

        setIsSearchingLocation(false);
      },
      () => {
        setGmapError("Unable to retrieve location");
        setIsSearchingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, [getAddressFromCoords]);

  // Dropdown Options
  const getOptions = useCallback(
    (fieldKey) => {
      switch (fieldKey) {
        case "taluk":
          return taluks.map((t) => ({
            id: t.TALUK_ID || t.id || t.taluk_id,
            label: t.TALUK_NAME || t.name || t.taluk_name || "Unknown Taluk",
          }));

        case "village":
          return villages.map((v) => {
            const villageName =
              v.VILLAGE_NAME || v.name || v.village_name || "Unknown Village";
            const talukName = v.TALUK_NAME || "";
            const districtName = v.DISTRICT_NAME || "";
            const suffix = [talukName, districtName].filter(Boolean).join(", ");

            return {
              id: v.VILLAGE_ID || v.id || v.village_id,
              label: suffix ? `${villageName}  —  ${suffix}` : villageName,
              rawName: villageName,
            };
          });

        case "area":
          return filteredAreas.map((a) => ({
            id: a.AREA_ID || a.id || a.area_id,
            label: a.AREA_NAME || a.name || a.area_name || "Unknown Area",
          }));

        default:
          return [];
      }
    },
    [taluks, villages, filteredVillages, filteredAreas]
  );

  // Field Change Handlers
  const updateField = useCallback(
    (key, value) => {
      setForm((prev) => {
        const newForm = { ...prev, [key]: value };

        if (key === "taluk") {
          newForm.village = "";
          newForm.area = "";
          newForm.areaName = "";
          setIsCustomAreaName(false);
        } else if (key === "village") {
          const selectedVillage = villages.find((v) => {
            const villageId = v.VILLAGE_ID || v.id || v.village_id;
            return String(villageId) === String(value);
          });

          const villageName =
            selectedVillage?.VILLAGE_NAME ||
            selectedVillage?.name ||
            selectedVillage?.village_name ||
            "";

          const villageTalukId =
            selectedVillage?.TALUK_ID ||
            selectedVillage?.taluk_id ||
            selectedVillage?.talukId;

          if (villageTalukId) {
            newForm.taluk = String(villageTalukId);
          }

          newForm.villageName = villageName;
          newForm.area = "";
          newForm.areaName = "";
          setIsCustomAreaName(false);
        } else if (key === "area") {
          const selectedArea = filteredAreas.find((a) => {
            const areaId = a.AREA_ID || a.id || a.area_id;
            return String(areaId) === String(value);
          });

          const areaName =
            selectedArea?.AREA_NAME ||
            selectedArea?.area_name ||
            selectedArea?.name ||
            "";
          newForm.areaName = areaName;
          setIsCustomAreaName(false);
        }

        return newForm;
      });

      setTouched((prev) => ({ ...prev, [key]: true }));
    },
    [filteredAreas, villages]
  );

  const handlePincodeChange = useCallback(
    (value) => {
      const numericValue = value.replace(/\D/g, "");
      const limitedValue = numericValue.slice(0, 6);
      updateField("pincode", limitedValue);
    },
    [updateField]
  );

  const handleSaveOnly = useCallback(() => handleSave(false), [handleSave]);
  const handleSaveAndNext = useCallback(() => handleSave(true), [handleSave]);

  // CommonFields Config
  const { renderDropdown, renderComboBox, renderTextInput } = CommonFields({
    form,
    touched,
    getFieldError: getError,
    handleBlur,
    updateField,
    getOptions,
  });

  return (
    <>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          lowContrast
          subtitle={error}
          onClose={() => setError("")}
          timeout={5000}
          style={{
            position: "fixed",
            top: "1rem",
            right: "1rem",
            zIndex: 9999,
            minWidth: "300px",
          }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          lowContrast
          onClose={() => setSuccess("")}
          timeout={3000}
          style={{
            position: "fixed",
            top: "1rem",
            right: "1rem",
            zIndex: 9999,
            minWidth: "300px",
          }}
        />
      )}

      {/* Google Maps Section */}
      <div>
        <h4 style={{ marginBottom: "0.5rem", color: "#525252" }}>
          Property Location
        </h4>

        {gmapError && (
          <div
            style={{
              backgroundColor: "#f8d7da",
              color: "#721c24",
              padding: "0.75rem",
              borderRadius: "4px",
              marginBottom: "1rem",
              border: "1px solid #f5c6cb",
            }}
          >
            {gmapError}
          </div>
        )}

        <div style={{ marginBottom: "1rem" }}>
          <Grid>
            <Column sm={4} md={8} lg={10}>
              <LoadScript
                googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                libraries={["places"]}
                onError={onLoadError}
                onLoad={onScriptLoad}
              >
                {isScriptLoaded && (
                  <Autocomplete
                    onLoad={onAutocompleteLoad}
                    onPlaceChanged={handlePlaceSelect}
                  >
                    <TextInput
                      id="mapSearch"
                      labelText="Search Location"
                      value={searchAddress}
                      onChange={(e) => setSearchAddress(e.target.value)}
                      placeholder="Search address..."
                      style={{ width: "100%" }}
                    />
                  </Autocomplete>
                )}
              </LoadScript>
            </Column>
            <Column sm={4} md={8} lg={6}>
              <Button
                kind="secondary"
                onClick={getCurrentLocation}
                disabled={isSearchingLocation}
                style={{ marginTop: "2rem", width: "100%" }}
              >
                {isSearchingLocation ? "Getting Location..." : "Current Location"}
              </Button>
            </Column>
          </Grid>
        </div>

        <LoadScript
          googleMapsApiKey={GOOGLE_MAPS_API_KEY}
          libraries={["places"]}
          onError={onLoadError}
          onLoad={onScriptLoad}
        >
          {isScriptLoaded && (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={mapCenter}
              zoom={14}
              onClick={onMapClick}
              onLoad={onMapLoad}
              options={{
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
                zoomControl: true,
                clickableIcons: false,
              }}
            >
              {markerPosition && (
                <Marker
                  position={markerPosition}
                  draggable={true}
                  onDragEnd={(e) => {
                    const lat = e.latLng.lat();
                    const lng = e.latLng.lng();
                    const newPosition = { lat, lng };
                    setMarkerPosition(newPosition);
                    setForm((prev) => ({
                      ...prev,
                      latitude: lat,
                      longitude: lng,
                    }));
                    if (geocoderRef.current) {
                      getAddressFromCoords(newPosition);
                    }
                  }}
                />
              )}
            </GoogleMap>
          )}
        </LoadScript>
      </div>

      <Grid>
        <Column sm={4} md={8} lg={8}>
          <TextInput
            id="latitude"
            labelText="Latitude"
            value={form.latitude || 0}
            readOnly
          />
        </Column>
        <Column sm={4} md={8} lg={8}>
          <TextInput
            id="longitude"
            labelText="Longitude"
            value={form.longitude || 0}
            readOnly
          />
        </Column>
      </Grid>

      <div>
        <Grid>
          <Column sm={4} md={8} lg={16}>
            <Tile style={{ backgroundColor: "white" }}>
              <Stack gap={6}>
                <TextInput
                  id="city"
                  labelText="District"
                  value="Kanyakumari"
                  readOnly
                />

                {/* 1. Search Village first */}
                <Grid>
                  <Column sm={4} md={8} lg={16}>
                    {renderComboBox(
                      "village",
                      "Search Village",
                      "Type to search any village (e.g. Thoothoor)...",
                      true
                    )}
                  </Column>
                </Grid>

                {/* 2. Taluk + Village Name */}
                <Grid>
                  <Column sm={4} md={8} lg={8}>
                    {renderDropdown("taluk", "Taluk", true)}
                  </Column>

                  <Column sm={4} md={8} lg={8}>
                    <TextInput
                      id="userVillageName"
                      labelText={requiredLabel("Village Name")}
                      value={form.userVillageName || ""}
                      onChange={(e) =>
                        updateField("userVillageName", e.target.value)
                      }
                      onBlur={() => handleBlur("userVillageName")}
                      placeholder="Enter village name (e.g. Thoothoor)"
                      invalid={
                        touched.userVillageName &&
                        !!getError("userVillageName")
                      }
                      invalidText={getError("userVillageName") || ""}
                    />
                  </Column>
                </Grid>

                <Grid>
                  <Column sm={4} md={8} lg={16}>
                    {renderDropdown("area", "Area", false)}
                  </Column>
                </Grid>

                {renderTextInput(
                  "landmark",
                  "Landmark",
                  "Enter nearby landmark",
                  true
                )}
                {renderTextInput(
                  "addressLine1",
                  "Address Line 1",
                  "Enter address line 1",
                  true
                )}
                {renderTextInput(
                  "addressLine2",
                  "Address Line 2",
                  "Enter address line 2",
                  false
                )}

                <div>
                  <TextInput
                    id="pincode"
                    labelText={requiredLabel("Pincode")}
                    value={form.pincode}
                    onChange={(e) => handlePincodeChange(e.target.value)}
                    onBlur={() => handleBlur("pincode")}
                    placeholder="Enter 6-digit pincode"
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    invalid={touched.pincode && !!getError("pincode")}
                    invalidText={getError("pincode")}
                    maxLength={6}
                  />
                </div>

                {CommonButtons && (
                  <CommonButtons
                    onBack={onBack}
                    onSave={handleSaveOnly}
                    onNext={handleSaveAndNext}
                    showBack
                    showSave
                    showNext
                    isSaveLoading={saving}
                  />
                )}
              </Stack>
            </Tile>
          </Column>
        </Grid>
      </div>
    </>
  );
};

export default LocationDetails;